/**
 * Phase 6 cross-cutting PR 4b — `useProactiveChecks` orchestrator hook.
 *
 * Runs the 5 proactive trigger detectors (shipped in PR 4) against
 * the athlete's current state, fires notifications for any new
 * trigger via `POST /api/notifications`, and writes back the
 * `_proactive_last_<type>` + `_last_dashboard_visit` debounce keys
 * via `mergeContext` so the same trigger doesn't double-fire on
 * subsequent dashboard loads the same day.
 *
 * **Conservative scope this PR (PR 4b):**
 *
 * The hook supports all 5 triggers, but the caller (today.tsx) only
 * populates `lastDashboardVisit` for now — the comeback trigger
 * fires from already-loaded data. compliance_drop, race_countdown,
 * streak_milestone, and pr_celebration require additional data
 * aggregation (plan_days streak count, weekly compliance %,
 * upcoming meets, race results) and land as follow-up PRs that wire
 * those data sources in. The detectors all exist in PR 4 and are
 * fully tested — only the caller-side wiring is incremental.
 *
 * **Safety gates:**
 *
 * 1. Top-level idempotency: if `_last_dashboard_visit === today`,
 *    the hook short-circuits entirely. One run per local-date.
 * 2. Per-type debouncing: each fired event has a
 *    `_proactive_last_<type>` debounce key. NU-2 (D1): an event may
 *    carry `debounceValue` (draft_next_week → the target week,
 *    season_wrap → the variant); the key is compared against and
 *    written with that value. Absent → today's date (the original
 *    daily debounce, unchanged for the 5 legacy triggers).
 * 3. Pref gate — per-EVENT since NU-2 (D4):
 *    `_notification_prefs[event.notificationType] === false` drops
 *    that event; the full short-circuit happens only when BOTH
 *    motivational_message AND plan_nudge are false. Behavior delta
 *    from PR 4b (documented in the NU-2 PR): a motivational-only
 *    opt-out now still writes `_last_dashboard_visit`, because the
 *    run proceeds for the other family.
 * 4. Single-effect with stable deps: runs once per mount + per
 *    data-ready transition. Will not re-run on re-renders.
 *
 * Sourced from:
 *   - vanilla `proactive-checks.js` (158 lines)
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §4
 */

import { useEffect, useMemo, useRef } from 'react';
import { useAuthFetch } from './useAuthFetch';
import { useAthleteContext } from './useAthleteContext';
import {
  detectAllTriggers,
  todayLocalIsoDate,
  type ProactiveDetectorInput,
  type ProactiveTriggerEvent,
} from '../lib/proactiveTriggers';
import {
  PROACTIVE_PRIORITY,
  PROACTIVE_DEBOUNCE_KEY_PREFIX,
  type ProactiveTriggerType,
} from '../lib/proactiveCopy';

export interface UseProactiveChecksArgs {
  /**
   * When false, the hook stays idle. Use this to gate on upstream
   * data being loaded (e.g., athleteContext.loading === false).
   */
  enabled: boolean;
  /** Detector input — caller computes from data hooks. */
  input: ProactiveDetectorInput;
}

export interface UseProactiveChecksResult {
  /**
   * The highest-priority detected event for this render (sorted per
   * `PROACTIVE_PRIORITY`). Null when no trigger conditions are
   * currently met OR when `enabled` is false.
   *
   * **PR 4f cross-module handoff**: `useMotivation` consumes this to
   * bias the LLM's daily motivation message. When `pr_celebration`
   * fires, the motivation card's message themes around the PR
   * instead of generic content. Matches vanilla
   * `motivational-coach.js:152-155`:
   *   if (state._proactiveHint) summary += ` IMPORTANT CONTEXT: …`
   *
   * The hint is RECOMPUTED on every render (detection is pure) — it
   * reflects current data, not "what we fired today." Same-day cache
   * hits in useMotivation render yesterday's themed message
   * regardless; the hint matters only on the day's first LLM call.
   */
  primaryHint: ProactiveTriggerEvent | null;
}

/**
 * Sort fired events by priority (highest first) per PROACTIVE_PRIORITY.
 * Vanilla used implicit "first wins" by call order — React makes the
 * order explicit so PR celebrations don't get swallowed by a same-day
 * comeback fire.
 */
function sortByPriority(events: ProactiveTriggerEvent[]): ProactiveTriggerEvent[] {
  const rank = new Map<ProactiveTriggerType, number>();
  PROACTIVE_PRIORITY.forEach((type, idx) => rank.set(type, idx));
  return [...events].sort((a, b) => (rank.get(a.type) ?? 999) - (rank.get(b.type) ?? 999));
}

export function useProactiveChecks({
  enabled,
  input,
}: UseProactiveChecksArgs): UseProactiveChecksResult {
  const fetchAuthed = useAuthFetch();
  const { raw: athleteContext, mergeContext, loading } = useAthleteContext();

  // PR 4f — compute detection synchronously outside the effect so
  // the result can be returned from the hook (consumed by
  // useMotivation for LLM prompt biasing). Detection is pure and
  // cheap; running it on every render is fine.
  const detectedEvents = useMemo(() => detectAllTriggers(input), [input]);
  const orderedEvents = useMemo(() => sortByPriority(detectedEvents), [detectedEvents]);
  const primaryHint: ProactiveTriggerEvent | null = orderedEvents[0] ?? null;

  // Guard: prevent double-fire from React StrictMode double-invoke +
  // ensure we only run once per `enabled` transition. The ref is the
  // last ISO date we ran for.
  const ranForDateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;

    const today = todayLocalIsoDate();

    // Already ran today (this mount cycle) — skip.
    if (ranForDateRef.current === today) return;

    // Pref gate (D4) — full short-circuit only when BOTH notification
    // families are disabled. A single-family opt-out proceeds so the
    // other family can still fire (per-event filtering below).
    const prefs = (athleteContext._notification_prefs ?? {}) as Record<string, boolean>;
    if (prefs.motivational_message === false && prefs.plan_nudge === false) {
      ranForDateRef.current = today;
      return;
    }

    // Top-level idempotency gate — if _last_dashboard_visit is
    // already today, all triggers have either fired or been checked
    // earlier today; nothing to do.
    const lastVisit = athleteContext._last_dashboard_visit;
    if (typeof lastVisit === 'string' && lastVisit === today) {
      ranForDateRef.current = today;
      return;
    }

    // Per-event pref gate (D4) + per-type debouncing (D1) — drop events
    // whose family is opted out, and events whose `_proactive_last_<type>`
    // key already equals their debounce value (today's date unless the
    // event carries an explicit `debounceValue`).
    const newEvents = orderedEvents.filter((e) => {
      if (prefs[e.notificationType] === false) return false;
      const key = `${PROACTIVE_DEBOUNCE_KEY_PREFIX}${e.type}`;
      return athleteContext[key] !== (e.debounceValue ?? today);
    });

    // Fire-and-forget notification writes for each new event.
    // (We don't await — the hook is a side-effect; failed writes
    // re-attempt on the next local-date.)
    (async () => {
      for (const event of newEvents) {
        try {
          await fetchAuthed('/api/notifications', {
            method: 'POST',
            body: JSON.stringify({
              title: event.title,
              message: event.message,
              type: event.notificationType,
              // NU-2 — the deep link rides as metadata; NotifRow routes
              // plan_nudge rows through sanitizeInternalHref(metadata.href).
              ...(event.href ? { metadata: { href: event.href } } : {}),
            }),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[proactive] Failed to fire notification:', err);
        }
      }

      // Update debounce keys + last_dashboard_visit. This is one
      // mergeContext call to keep the persistence atomic.
      const updates: Record<string, string> = {
        _last_dashboard_visit: today,
      };
      // NP-1 — stamp the athlete's IANA timezone for the server-side nudge
      // scheduler (NP-2): the detectors are local-day-of-week sensitive and
      // the server stores no timezone anywhere else. Best-effort — a missing
      // value falls back server-side (America/Los_Angeles).
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) updates._timezone = tz;
      } catch {
        // best effort
      }
      for (const event of newEvents) {
        updates[`${PROACTIVE_DEBOUNCE_KEY_PREFIX}${event.type}`] = event.debounceValue ?? today;
      }
      mergeContext(updates);
    })();

    // Mark this mount as run for today so re-renders don't re-fire.
    ranForDateRef.current = today;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loading, fetchAuthed, mergeContext, orderedEvents]);

  return { primaryHint };
}
